import React from 'react';
import { Clock, Award, MapPin, Mail, Phone, ExternalLink } from 'lucide-react';
import type { CandidateMatch } from '../shared/types';

interface SearchResultsProps {
  matches: CandidateMatch[];
  searchTime: number;
  isLoading: boolean;
}

export function SearchResults({ matches, searchTime, isLoading }: SearchResultsProps) {
  if (isLoading) {
    return (
      <div className="bg-gray-800 border border-gray-700 rounded-lg p-6">
        <div className="flex items-center justify-center space-x-2">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
          <span className="text-gray-300">Searching students...</span>
        </div>
      </div>
    );
  }

  if (matches.length === 0) {
    return (
      <div className="bg-gray-800 border border-gray-700 rounded-lg p-6 text-center">
        <p className="text-gray-400">No students found. Try adjusting your search criteria.</p>
      </div>
    );
  }

  const getScoreColor = (score: number) => {
    if (score >= 0.8) return 'text-green-400 bg-green-500/20';
    if (score >= 0.6) return 'text-yellow-400 bg-yellow-500/20';
    if (score >= 0.4) return 'text-orange-400 bg-orange-500/20';
    return 'text-red-400 bg-red-500/20';
  };

  const getFitBadgeColor = (fit: string) => {
    switch (fit) {
      case 'excellent': return 'bg-green-500/20 text-green-400 border-green-500/30';
      case 'good': return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
      case 'fair': return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30';
      default: return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-gray-800 border border-gray-700 rounded-lg p-4">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-bold text-white">
            Search Results ({matches.length} students)
          </h2>
          <div className="flex items-center space-x-1 text-sm text-gray-400">
            <Clock className="w-4 h-4" />
            <span>Search completed in {searchTime}ms</span>
          </div>
        </div>
      </div>

      {matches.map((match) => (
        <div key={match.candidate.id} className="bg-gray-800 border border-gray-700 rounded-lg p-6 hover:border-gray-600 transition-colors">
          <div className="flex justify-between items-start mb-4">
            <div className="flex-1">
              <div className="flex items-center space-x-3 mb-2">
                <h3 className="text-xl font-bold text-white">{match.candidate.name}</h3>
                <span className={`px-3 py-1 rounded-full text-sm font-medium border ${getFitBadgeColor(match.matchDetails.overallFit)}`}>
                  {match.matchDetails.overallFit} fit
                </span>
              </div>
              <p className="text-lg text-blue-400 mb-1">{match.candidate.title}</p>
              <div className="flex items-center space-x-4 text-sm text-gray-400">
                <div className="flex items-center space-x-1">
                  <MapPin className="w-4 h-4" />
                  <span>{match.candidate.location}</span>
                </div>
                <div className="flex items-center space-x-1">
                  <Award className="w-4 h-4" />
                  <span>{match.candidate.yearsOfExperience} years experience</span>
                </div>
              </div>
            </div>
            
            <div className="text-center">
              <div className={`text-2xl font-bold rounded-lg px-3 py-2 ${getScoreColor(match.score)}`}>
                {Math.round(match.score * 100)}%
              </div>
              <p className="text-xs text-gray-400 mt-1">Match Score</p>
            </div>
          </div>

          <div className="mb-4">
            <p className="text-gray-300">{match.candidate.summary}</p>
          </div>

          <div className="mb-4">
            <h4 className="font-semibold text-white mb-2">Match Analysis</h4>
            <p className="text-gray-300 text-sm">{match.explanation}</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <h4 className="font-semibold text-white mb-2">Skills Match</h4>
              <div className="flex flex-wrap gap-2">
                {match.matchDetails.skillsMatch.slice(0, 6).map((skillMatch, index) => (
                  <span
                    key={index}
                    className={`px-2 py-1 rounded-full text-xs font-medium border ${
                      skillMatch.match === 'exact' ? 'bg-green-500/20 text-green-400 border-green-500/30' :
                      skillMatch.match === 'similar' ? 'bg-blue-500/20 text-blue-400 border-blue-500/30' :
                      'bg-gray-500/20 text-gray-400 border-gray-500/30'
                    }`}
                  >
                    {skillMatch.skill} ({Math.round(skillMatch.confidence * 100)}%)
                  </span>
                ))}
                {match.matchDetails.skillsMatch.length > 6 && (
                  <span className="px-2 py-1 rounded-full text-xs font-medium bg-gray-700 text-gray-400">
                    +{match.matchDetails.skillsMatch.length - 6} more
                  </span>
                )}
              </div>
            </div>

            <div>
              <h4 className="font-semibold text-white mb-2">Top Skills</h4>
              <div className="flex flex-wrap gap-2">
                {match.candidate.skills.slice(0, 5).map((skill, index) => (
                  <span
                    key={index}
                    className="px-2 py-1 rounded-full text-xs font-medium bg-blue-500/20 text-blue-400 border border-blue-500/30"
                  >
                    {skill}
                  </span>
                ))}
                {match.candidate.skills.length > 5 && (
                  <span className="px-2 py-1 rounded-full text-xs font-medium bg-gray-700 text-gray-400">
                    +{match.candidate.skills.length - 5} more
                  </span>
                )}
              </div>
            </div>
          </div>

          <div className="border-t border-gray-700 pt-4">
            <div className="flex flex-wrap items-center gap-4 text-sm">
              <div className="flex items-center space-x-1 text-gray-400">
                <Mail className="w-4 h-4" />
                <span>{match.candidate.email}</span>
              </div>
              {match.candidate.phone && (
                <div className="flex items-center space-x-1 text-gray-400">
                  <Phone className="w-4 h-4" />
                  <span>{match.candidate.phone}</span>
                </div>
              )}
              {match.candidate.linkedinUrl && (
                <a
                  href={match.candidate.linkedinUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center space-x-1 text-blue-400 hover:text-blue-300"
                >
                  <ExternalLink className="w-4 h-4" />
                  <span>LinkedIn</span>
                </a>
              )}
              {match.candidate.githubUrl && (
                <a
                  href={match.candidate.githubUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center space-x-1 text-blue-400 hover:text-blue-300"
                >
                  <ExternalLink className="w-4 h-4" />
                  <span>GitHub</span>
                </a>
              )}
            </div>
          </div>

          <div className="mt-4 pt-4 border-t border-gray-700">
            <h4 className="font-semibold text-white mb-2">Recent Experience</h4>
            {match.candidate.experience.slice(0, 2).map((exp) => (
              <div key={exp.id} className="mb-3 last:mb-0">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="font-medium text-white">{exp.position}</p>
                    <p className="text-sm text-gray-400">{exp.company}</p>
                  </div>
                  <p className="text-sm text-gray-400">
                    {exp.startDate} - {exp.endDate || 'Present'}
                  </p>
                </div>
                <p className="text-sm text-gray-300 mt-1">{exp.description}</p>
                {exp.technologies && (
                  <div className="flex flex-wrap gap-1 mt-2">
                    {exp.technologies.map((tech, index) => (
                      <span
                        key={index}
                        className="px-2 py-1 rounded text-xs bg-gray-700 text-gray-300"
                      >
                        {tech}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
